import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { RolesService } from '../roles.service';
import { ModuleForm } from '../components/permission-selector/permission-selector.component';
import { HeaderAction } from '../../../shared/components/page-header/page-header.component';
import { FieldValidationState } from '../../users/users.model';

@Component({
  selector: 'app-create-role',
  standalone: false,
  templateUrl: './create-role.component.html',
  styleUrls: ['./create-role.component.scss'],
})
export class CreateRoleComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private rolesService = inject(RolesService);
  private router = inject(Router);

  form: FormGroup;
  isSubmitting = false;
  submitted = false;
  nameValidationState: FieldValidationState = { status: 'idle' };
  private nameSub?: Subscription;

  // Header Actions configuration
  headerActions: HeaderAction[] = [
    { label: 'Cancel', variant: 'secondary' },
    { label: '+ Create Role', variant: 'primary' }
  ];

  // Templates list
  templates = ['Blank', 'Read-only', 'Region operator', 'Auditor'];
  selectedTemplate = 'Blank';

  // Prefilled permissions when navigating from duplicate
  initialPermissions: { [code: string]: boolean } = {};

  get hasInitialPermissions(): boolean {
    return Object.keys(this.initialPermissions).length > 0;
  }

  // State trackers from child PermissionSelectorComponent
  currentModules: ModuleForm[] = [];
  grantedCount = 0;
  moduleCountsMap: { [name: string]: { granted: number, total: number } } = {
    'User Management': { granted: 0, total: 5 },
    'Roles & Permissions': { granted: 0, total: 4 },
    'Geographic Nodes': { granted: 0, total: 4 },
    'Channels': { granted: 0, total: 4 },
    'Dispatch & Operations': { granted: 0, total: 3 },
    'Reports & Analytics': { granted: 0, total: 2 },
    'System Settings': { granted: 0, total: 0 }
  };

  readonly MODULE_NAMES = [
    'User Management',
    'Roles & Permissions',
    'Geographic Nodes',
    'Channels',
    'Dispatch & Operations',
    'Reports & Analytics',
    'System Settings'
  ];

  constructor() {
    this.form = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      active: [true],
    });
  }

  ngOnInit(): void {
    const nav = this.router.getCurrentNavigation();
    const state = nav?.extras?.state ?? history.state;
    if (state?.initialPermissions) {
      this.initialPermissions = state.initialPermissions;
    }

    this.nameSub = this.form.get('name')!.valueChanges.pipe(
      tap((val: string) => {
        this.nameValidationState = val ? { status: 'checking' } : { status: 'idle' };
      }),
      debounceTime(500),
      switchMap((val: string) => {
        if (!val) return of(null);
        return this.rolesService.checkNameUniqueness(val);
      })
    ).subscribe({
      next: (res) => {
        if (!res) return;
        if (res.available) {
          this.nameValidationState = { status: 'valid', message: '✓ Available' };
        } else {
          this.nameValidationState = {
            status: 'conflict',
            message: 'A role with this name already exists',
            conflictUser: res.role ? { full_name: res.role.name, id: res.role.id } : undefined
          };
        }
      },
      error: () => {
        this.nameValidationState = { status: 'error', message: 'Error checking name availability' };
      }
    });
  }

  ngOnDestroy(): void {
    this.nameSub?.unsubscribe();
  }

  // Receives changes from child PermissionSelectorComponent
  onModulesChange(modules: ModuleForm[]) {
    this.currentModules = modules;
  }

  onPermissionCountChange(count: number) {
    this.grantedCount = count;
  }

  onModuleCountsChange(countsMap: { [name: string]: { granted: number, total: number } }) {
    this.moduleCountsMap = countsMap;
  }

  selectTemplate(tpl: string) {
    this.selectedTemplate = tpl;
  }

  // Handle header actions
  onHeaderActionClick(action: HeaderAction) {
    if (action.label === 'Cancel') {
      this.cancel();
    } else if (action.label === '+ Create Role') {
      this.submit();
    }
  }

  cancel() {
    this.router.navigate(['/roles']);
  }

  async submit() {
    this.submitted = true;
    if (
      this.form.invalid ||
      this.nameValidationState.status === 'conflict' ||
      this.nameValidationState.status === 'checking'
    ) {
      return;
    }

    this.isSubmitting = true;
    try {
      const payload = {
        name: this.form.value.name,
        description: this.form.value.description || '',
        active: this.form.value.active,
        modules: this.currentModules.map(m => ({
          name: m.name,
          has_read: m.has_read,
          has_create: m.has_create,
          has_update: m.has_update,
          has_delete: m.has_delete,
          permissions: m.permissions,
        })),
      };

      await new Promise<void>((resolve, reject) => {
        this.rolesService.create(payload).subscribe({ next: () => resolve(), error: reject });
      });
      this.router.navigate(['/roles']);
    } catch (e: any) {
      if (e?.status === 409) {
        this.nameValidationState = { status: 'conflict', message: 'A role with this name already exists' };
      } else {
        console.error('Create role failed', e);
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  // Form issues tracker
  getValidationIssues(): string[] {
    const issues: string[] = [];
    const nameCtrl = this.form.get('name');
    if (nameCtrl && nameCtrl.errors?.['required']) {
      issues.push('Role name is required');
    } else if (this.nameValidationState.status === 'conflict') {
      issues.push('Role name is already taken');
    }
    return issues;
  }
}
